import { useQuery } from '@tanstack/react-query'
import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createApiClient, isApiError, type ApiClient, type ServiceSignal } from '../api/client'
import { type Membership, MeResponse, type PasswordChangeStatus } from '@rp/contracts'
import type { AuthClient } from '../auth/client'
import { StatusBar } from '../design'
import { forgetPushSubscription, unsubscribeDevicePush } from '../features/live/pushSignOut'
import { clearUserDrafts, sessionDraftStorage } from '../features/reply/draftStorage'
import { SESSION_COPY } from './copy'
import { TenantPicker } from './TenantPicker'
import { readStoredTenant, storeTenant } from './tenantStorage'
import { useAuthState } from './useAuthState'

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'no_access' }
  | { status: 'error'; message: string }
  | { status: 'password_change_required'; me: MeResponse }
  | { status: 'choosing_tenant'; me: MeResponse }
  | { status: 'ready'; me: MeResponse; membership: Membership }

export type SessionValue = SessionState & {
  signOut: () => Promise<void>
  chooseTenant: (tenantId: string) => void
  clearTenant: () => void
  retry: () => void
  changePassword: (currentPassword: string, newPassword: string) => Promise<PasswordChangeStatus>
  api: ApiClient
}

export const SessionContext = createContext<SessionValue | null>(null)

type MeFailure = { status: 'no_access' } | { status: 'error'; message: string }

const meFailure = (error: unknown): MeFailure => {
  if (isApiError(error) && error.code === 'FORBIDDEN') return { status: 'no_access' }
  return { status: 'error', message: isApiError(error) ? error.message : SESSION_COPY.meUnavailable }
}

const memoryStorage = (): Pick<Storage, 'getItem' | 'setItem'> => {
  try {
    return window.localStorage
  } catch {
    const map = new Map<string, string>()
    return { getItem: (k) => map.get(k) ?? null, setItem: (k, v) => void map.set(k, v) }
  }
}

export interface SessionProviderProps {
  auth: AuthClient
  baseUrl: string
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  requestedTenantId?: string | null
  onSignal?: (signal: ServiceSignal) => void
  children: ReactNode
}

export const SessionProvider = ({
  auth,
  baseUrl,
  storage,
  requestedTenantId = null,
  onSignal,
  children,
}: SessionProviderProps) => {
  const store = useMemo(() => storage ?? memoryStorage(), [storage])
  const [tenantId, setTenantId] = useState<string | null>(null)
  const tenantRef = useRef<string | null>(null)
  tenantRef.current = tenantId
  const autoSelectedForRef = useRef<string | null>(null)
  const clearTenant = useCallback(() => setTenantId(null), [])
  const { user, initialising, restoreError, signOutError, signOut, retryRestore } = useAuthState(auth, clearTenant)

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl,
        getTenantId: () => tenantRef.current,
        ...(onSignal === undefined ? {} : { onSignal }),
        onUnauthenticated: () => {
          setTenantId(null)
          auth.forget()
          void unsubscribeDevicePush()
        },
      }),
    [auth, baseUrl, onSignal],
  )

  const meQuery = useQuery({
    queryKey: ['me', user?.id ?? null],
    enabled: user !== null,
    queryFn: async () => (await api.request(MeResponse, { method: 'GET', path: '/v1/me' })).data,
  })

  const me = meQuery.data ?? null

  useEffect(() => {
    if (me === null || user === null || tenantId !== null) return
    if (autoSelectedForRef.current === user.id) return
    autoSelectedForRef.current = user.id
    const stored = readStoredTenant(store, user.id)
    const requested = me.memberships.find((membership) => membership.tenantId === requestedTenantId)
    const remembered = me.memberships.find((membership) => membership.tenantId === stored)
    const only = me.memberships.length === 1 ? me.memberships[0] : undefined
    const chosen = requested ?? remembered ?? only
    if (chosen === undefined) return
    setTenantId(chosen.tenantId)
    if (requested !== undefined) storeTenant(store, user.id, requested.tenantId)
  }, [me, requestedTenantId, store, tenantId, user])

  const chooseTenant = useCallback(
    (next: string) => {
      setTenantId(next)
      if (user !== null) storeTenant(store, user.id, next)
    },
    [store, user],
  )

  const membership = me?.memberships.find((entry) => entry.tenantId === tenantId) ?? null

  const refetchMe = meQuery.refetch
  const retry = useCallback(
    () => (restoreError === null ? void refetchMe() : retryRestore()),
    [refetchMe, restoreError, retryRestore],
  )

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      const status = await auth.changePassword(currentPassword, newPassword)
      await refetchMe()
      return status
    },
    [auth, refetchMe],
  )

  const signOutEverywhere = useCallback(async () => {
    const signingOut = user?.id ?? null
    await forgetPushSubscription(api)
    await signOut()
    if (signingOut !== null && auth.current() === null) clearUserDrafts(sessionDraftStorage(), signingOut)
  }, [api, auth, signOut, user])

  const value: SessionValue = useMemo(() => {
    const controls = { signOut: signOutEverywhere, chooseTenant, clearTenant, retry, changePassword, api }
    if (restoreError !== null) return { status: 'error', message: restoreError, ...controls }
    if (initialising || (user !== null && me === null && meQuery.isPending)) return { status: 'loading', ...controls }
    if (user !== null && me === null && meQuery.isError) return { ...meFailure(meQuery.error), ...controls }
    if (user === null || me === null) return { status: 'signed_out', ...controls }
    if (me.passwordChangeRequired) return { status: 'password_change_required', me, ...controls }
    if (me.memberships.length === 0) return { status: 'no_access', ...controls }
    if (membership === null) return { status: 'choosing_tenant', me, ...controls }
    return { status: 'ready', me, membership, ...controls }
  }, [
    api,
    changePassword,
    chooseTenant,
    clearTenant,
    initialising,
    me,
    membership,
    meQuery.error,
    meQuery.isError,
    meQuery.isPending,
    restoreError,
    retry,
    signOutEverywhere,
    user,
  ])

  return (
    <SessionContext.Provider value={value}>
      {signOutError === null ? null : <StatusBar tone="danger" live="alert" title={signOutError} />}
      {value.status === 'choosing_tenant' ? (
        <TenantPicker memberships={value.me.memberships} onChoose={chooseTenant} onSignOut={() => void signOutEverywhere()} />
      ) : (
        children
      )}
    </SessionContext.Provider>
  )
}
